import React from "react";

import {
  MyTeamsSection,
  ModuleMenu,
  TeamsNavSection,
  type ModuleMenuOption,
  type ModuleMenuSection,
} from "#app/components/navigation/module-menu";
import { useSidebarNav } from "#app/components/navigation/use-sidebar-nav";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { CreateTeamDialog } from "./create-team-dialog";

export function TeamsModuleMenu() {
  const { navigate } = useSidebarNav();
  const { refetch: refetchAppData } = useAppData();
  const { teams } = useWorkspaceData();
  const canCreateTeam = usePermission("manage.teams");
  // Same grant-driven gate as the rail's Invite tile and the Members page.
  const canInvite = useCanInvite();
  const closeSidebar = useCloseSidebarIfMobile();
  const [createTeamOpen, setCreateTeamOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);

  // Order/inclusion here is the instance default; user preferences will reorder/
  // filter by section id. The Teams section lists all accessible teams (decision
  // #4), skipped only when there's nothing to show or do (no teams AND no create
  // permission).
  const sections = React.useMemo<ModuleMenuSection[]>(() => {
    const list: ModuleMenuSection[] = [
      { id: "nav", Component: TeamsNavSection },
    ];
    if (teams.length > 0 || canCreateTeam) {
      list.push({ id: "my-teams", Component: MyTeamsSection });
    }
    return list;
  }, [teams.length, canCreateTeam]);

  const options = React.useMemo<ModuleMenuOption[]>(
    () => [
      ...(canCreateTeam
        ? [
            {
              id: "create-team",
              // Matches TeamsNavSection's "All Teams" icon.
              icon: "users" as const,
              label: "Create team",
              onClick: () => {
                closeSidebar();
                setCreateTeamOpen(true);
              },
            },
          ]
        : []),
      ...(canInvite
        ? [
            {
              id: "invite-member",
              icon: "user-plus" as const,
              label: "Invite member",
              onClick: () => {
                closeSidebar();
                setInviteOpen(true);
              },
            },
          ]
        : []),
    ],
    [canCreateTeam, canInvite, closeSidebar],
  );

  return (
    <>
      <ModuleMenu title="Teams" sections={sections} options={options} />
      {canCreateTeam && (
        <CreateTeamDialog
          open={createTeamOpen}
          onOpenChange={setCreateTeamOpen}
          onCreated={async (teamId) => {
            // App data feeds the module menu's team list (createTeam only
            // refetches TeamsListData); land on the new team's settings page
            // like the workflow flow does.
            await refetchAppData();
            navigate(`/settings/teams/${teamId}`);
          }}
        />
      )}
      {canInvite && (
        <InviteMembersDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      )}
    </>
  );
}
